import { useState, useEffect } from 'react';
import Select from 'react-select';
import './ElectionResults.css';
import DownloadButton from '../../components/DownloadButton/DownloadButton';
import { getJsonUrl } from '../../utils/fileDownloader';


const ElectionResults = () => {
  const [data, setData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedElection, setSelectedElection] = useState(null);
  const [fileGroups, setFileGroups] = useState([]);

  // Fetch mock data on mount
  useEffect(() => {
    fetch(getJsonUrl())
      .then(res => res.json())
      .then(data => setData(data.pastElectionResults || []))
      .catch(err => console.error('Error fetching data:', err));
  }, []);

  // Update file groups when year and election are selected
  useEffect(() => {
    if (selectedYear && selectedElection) {
      const yearData = data.find(y => y.year === selectedYear.value);
      if (yearData) {
        const electionData = yearData.elections.find(e => e.type === selectedElection.value);
        setFileGroups(electionData ? electionData.fileGroups : []);
      }
    } else {
      setFileGroups([]);
    }
  }, [selectedYear, selectedElection, data]);

  // Extract years for first dropdown
  const yearOptions = data.map(y => ({ value: y.year, label: y.year.toString() }));

  // Extract election types for second dropdown (only if year is selected)
  const electionOptions = selectedYear
    ? data.find(y => y.year === selectedYear.value)?.elections.map(e => ({ value: e.type, label: e.type })) || []
    : [];

  // Custom styles for React Select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      width: '20rem',
      height: '2.5rem',
      minHeight: '2.5rem',
      borderColor: state.isFocused ? 'var(--primary-color)' : '#ccc',
      boxShadow: state.isFocused ? '0 0 0 1px var(--primary-color)' : 'none',
      '&:hover': {
        borderColor: 'var(--primary-color)'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 1rem'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: '#ccc',
      width: '2px'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#666',
      padding: '0 0.75rem'
    })
  };

  return (
    <div className="page-container election-results">
      <h2 className="vr-header">Past Election Results</h2>
      <p className="intro">
        Welcome to Ohio's Data Portal, your gateway to comprehensive and interactive information
        directly sourced from the Ohio Secretary of State's website. The Past Election Results page allows you to delve into
        historical election data by year and election. All files are in Excel format and available for download.
      </p>

      <section className="section">
        <h3 className="section-title">Statewide Election Results by Year</h3>

        <div className="year-search-controls">
          {/* First Dropdown: Select Year */}
          <Select
            options={yearOptions}
            value={selectedYear}
            onChange={(option) => {
              setSelectedYear(option);
              setSelectedElection(null); // Reset election when year changes
            }}
            placeholder="Select Year"
            styles={customStyles}
            isSearchable={false}
          />

          {/* Second Dropdown: Select Election Type after year is selected */}
          {selectedYear && (
            <Select
              options={electionOptions}
              value={selectedElection}
              onChange={setSelectedElection}
              placeholder="Select Election"
              styles={customStyles}
              isSearchable={false}
            />
          )}
        </div>

        {/* Display dynamic title when both year and election are selected */}
        {selectedYear && selectedElection && (
          <h4 className="election-title">{selectedElection.label}</h4>
        )}

        {/* Render file groups with optional descriptions */}
        {fileGroups.length > 0 && fileGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="file-group">
            {group.description && (
              <p className="group-description">{group.description}</p>
            )}
            <div className="download-button-grid">
              {group.files.map(file => (
                <DownloadButton
                  key={file.blobPath}
                  blobPath={file.blobPath}
                  displayName={file.displayName}
                >
                  {file.displayName}
                </DownloadButton>
              ))}
            </div>
          </div>
        ))}
      </section>
      
      {/* Note abou tmissing files shown only before any selections are made */}
        {!selectedYear && !selectedElection && (
          <p className="files-note">
            <em>Note: Historical election result files are not available for 1941, 1943, 1945, 1963, and 1991.</em>
          </p>
        )}
    </div>
  );
};

export default ElectionResults;
